import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CompositeChart } from "./components/composite-chart";
import {
  CompositionMathPanel,
  CompositionMathTrigger,
} from "./components/composition-math-tooltip";
import { CreateCompositePanel } from "./components/create-composite-panel";
import { ExploreCTA } from "./components/explore-cta";
import { HyperliquidMarketsModal } from "./components/hyperliquid-markets-modal";
import { PerpChartView, PERP_COLOR } from "./components/perp-chart-view";
import { PerpsPanel, type PerpPanelMode } from "./components/perps-panel";
import { PolymarketMarketsModal } from "./components/polymarket-markets-modal";
import {
  PredictionChartView,
  PRED_COLOR,
} from "./components/prediction-chart-view";
import {
  PredictionsPanel,
  type PredPanelMode,
} from "./components/predictions-panel";
import { RiskRewardTiles } from "./components/risk-reward-tiles";
import { ScenariosTable } from "./components/scenarios-table";
import { DesktopCompositeView } from "./components/desktop/desktop-composite-view";
import { DesktopLanding } from "./components/desktop/desktop-landing";
import { GoldSweep } from "./components/desktop/gold-sweep";
import { MobileApp } from "./components/mobile/mobile-app";
import { TopBar } from "./components/top-bar";
import { useCompositeExecution } from "./hooks/use-composite-execution";
import { useIsMobile } from "./hooks/use-media-query";
import { fetchBtcPerp, fetchBtcPrediction } from "./lib/auto-simulate";
import {
  composePayoff,
  liqPrice,
  maxGain,
  maxLoss,
  perpPnl,
  predPnl,
  type PerpLeg,
  type PredictionLeg,
} from "./lib/composePayoff";
import {
  buildScenarios,
  classifyStrategy,
  findBreakevens,
  type Scenario,
} from "./lib/scenarios";
import type { SelectedPerp, SelectedPred } from "./types";

// --------------------------------------------------------------------------
// Shared tokens
// --------------------------------------------------------------------------

const mono9: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 500,
  color: "var(--fg-meta)",
};

const sectionHeader: CSSProperties = {
  ...mono9,
  padding: "8px 12px",
  minHeight: 44,
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid var(--line-2)",
};

const panelHeader: CSSProperties = {
  ...mono9,
  padding: "8px 12px",
  minHeight: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  borderBottom: "1px solid var(--line-2)",
};

function PanelTitle({
  label,
  color,
}: {
  label: string;
  color: string;
}) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 10,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        fontWeight: 600,
        color,
        background: `${color}14`,
        border: `1px solid ${color}66`,
        padding: "4px 10px",
        borderRadius: 2,
      }}
    >
      {label}
    </span>
  );
}

function createCompositeBtn(
  enabled: boolean,
  active: boolean = false,
): CSSProperties {
  const bg = active ? "var(--fg-max)" : enabled ? "var(--fg-hi)" : "transparent";
  return {
    fontFamily: "var(--f-mono)",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    fontWeight: 600,
    color: enabled ? "var(--bg-0)" : "var(--fg-dim)",
    background: bg,
    border: enabled ? "1px solid var(--fg-hi)" : "1px solid var(--line-3)",
    padding: "5px 14px",
    cursor: enabled ? "pointer" : "not-allowed",
    borderRadius: 2,
    height: 26,
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    transition: "background 160ms ease, color 160ms ease",
    boxShadow: enabled ? "0 0 18px rgba(255,255,255,0.16)" : "none",
  };
}

const raisedCompositeCard: CSSProperties = {
  flex: 7.5,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  background: `
    linear-gradient(180deg, rgba(255,255,255,0.035) 0%, transparent 8%),
    linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 60%, var(--bg-1) 100%)
  `,
  borderTop: "1px solid rgba(255,255,255,0.14)",
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.09),
    inset 0 2px 0 -1px rgba(255,255,255,0.03),
    0 -1px 0 rgba(0,0,0,0.4),
    0 -10px 28px -6px rgba(0,0,0,0.55)
  `,
};

const placeholderStyle: CSSProperties = {
  ...mono9,
  flex: 1,
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px dashed var(--line-3)",
  color: "var(--fg-dim)",
  margin: 12,
  borderRadius: 2,
  textAlign: "center",
};

function Placeholder({
  label,
  style,
}: {
  label: string;
  style?: CSSProperties;
}): ReactNode {
  return <div style={{ ...placeholderStyle, ...style }}>{label}</div>;
}

// --------------------------------------------------------------------------
// Leg defaults
// --------------------------------------------------------------------------

function defaultPerpSize(markPx: number): number {
  if (!isFinite(markPx) || markPx <= 0) return 1;
  const raw = 1000 / markPx;
  return parseFloat(raw.toPrecision(4));
}

function defaultPerpLeg(perp: SelectedPerp): PerpLeg {
  const leverage = Math.min(10, Math.max(1, perp.maxLeverage || 10));
  const size = defaultPerpSize(perp.markPx);
  return {
    side: "long",
    size,
    entry: perp.markPx,
    leverage,
    liquidation: liqPrice("long", perp.markPx, leverage),
  };
}

function defaultPredLeg(
  pred: SelectedPred,
  perp: SelectedPerp | null,
): PredictionLeg {
  const yes = pred.yesPrice ?? 0.5;
  return {
    outcome: "yes",
    shares: 100,
    entryCents: parseFloat((yes * 100).toFixed(1)),
    threshold: perp?.markPx ?? 0,
    direction: "above",
  };
}

// --------------------------------------------------------------------------
// Inner section header with right-hand metadata
// --------------------------------------------------------------------------

function ChartHeader({
  title,
  subtitle,
  color,
  right,
}: {
  title: string;
  subtitle?: ReactNode;
  color?: string;
  right?: ReactNode;
}) {
  return (
    <div
      style={{
        ...sectionHeader,
        justifyContent: "space-between",
        color: color ?? "var(--fg-meta)",
        gap: 10,
      }}
    >
      <span>{title}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {subtitle}
        {right}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------
// App
// --------------------------------------------------------------------------

export function App() {
  const isMobile = useIsMobile();

  const [perpsModalOpen, setPerpsModalOpen] = useState(false);
  const [predsModalOpen, setPredsModalOpen] = useState(false);
  const [selectedPerp, setSelectedPerp] = useState<SelectedPerp | null>(null);
  const [selectedPred, setSelectedPred] = useState<SelectedPred | null>(null);

  const [perpMode, setPerpMode] = useState<PerpPanelMode>("existing");
  const [predMode, setPredMode] = useState<PredPanelMode>("existing");
  const [perpLeg, setPerpLeg] = useState<PerpLeg | null>(null);
  const [predLeg, setPredLeg] = useState<PredictionLeg | null>(null);

  // "CREATE COMPOSITE" review panel (right aside, below the simulate accordion).
  const [createOpen, setCreateOpen] = useState(false);
  // "HOW IT WORKS" math panel (left aside, below the simulate accordion).
  const [mathOpen, setMathOpen] = useState(false);
  // Desktop has two stages — landing (with inline market selection) and
  // trading. Mobile renders its own layout regardless of this state.
  const [desktopStage, setDesktopStage] = useState<"landing" | "trading">(
    "landing",
  );
  // True while the gold-sweep transition from selection → trading is playing.
  const [revealingComposite, setRevealingComposite] = useState(false);

  // Scenario highlight — hovering a row sets transient, clicking pins.
  const [hoverScenario, setHoverScenario] = useState<Scenario | null>(null);
  const [pinnedScenario, setPinnedScenario] = useState<Scenario | null>(null);

  useEffect(() => {
    setPerpLeg(null);
    setPerpMode("existing");
    setCreateOpen(false);
    setMathOpen(false);
  }, [selectedPerp?.coin]);

  useEffect(() => {
    setPredLeg(null);
    setPredMode("existing");
    setCreateOpen(false);
    setMathOpen(false);
  }, [selectedPred?.id]);

  const initPerpLeg = useCallback(() => {
    if (!selectedPerp) return;
    setPerpLeg(defaultPerpLeg(selectedPerp));
  }, [selectedPerp]);

  const initPredLeg = useCallback(() => {
    if (!selectedPred) return;
    setPredLeg(defaultPredLeg(selectedPred, selectedPerp));
  }, [selectedPred, selectedPerp]);

  const payoff = useMemo(() => {
    if (!perpLeg || !predLeg || !selectedPerp) return null;
    const mark = selectedPerp.markPx;
    if (!isFinite(mark) || mark <= 0) return null;
    return composePayoff(perpLeg, predLeg, {
      min: mark * 0.8,
      max: mark * 1.2,
      steps: 200,
    });
  }, [perpLeg, predLeg, selectedPerp]);

  const derived = useMemo(() => {
    if (!payoff || !perpLeg || !predLeg || !selectedPerp) return null;
    const mark = selectedPerp.markPx;
    return {
      mark,
      atMark: perpPnl(perpLeg, mark) + predPnl(predLeg, mark),
      maxGain: maxGain(payoff),
      maxLoss: maxLoss(payoff),
      breakevens: findBreakevens(payoff),
      scenarios: buildScenarios(perpLeg, predLeg, mark),
      strategy: classifyStrategy(perpLeg, predLeg),
    };
  }, [payoff, perpLeg, predLeg, selectedPerp]);

  const activeScenario = pinnedScenario ?? hoverScenario;

  // --------------------------------------------------------------------------
  // Sub-renderers — shared between desktop and mobile layouts
  // --------------------------------------------------------------------------

  const [focusCount, setFocusCount] = useState(0);
  const inFocus = focusCount > 0;
  const shouldDim = derived && !inFocus;
  const dim: CSSProperties = {
    opacity: shouldDim ? 0.5 : 1,
    transition: "opacity 220ms ease, box-shadow 240ms ease",
  };
  const dimHandlers = derived
    ? {
        onMouseEnter: () => setFocusCount((c) => c + 1),
        onMouseLeave: () => setFocusCount((c) => Math.max(0, c - 1)),
      }
    : {};

  // Group-outline visuals: when the composite is complete and the side sections
  // dim, outline each side (perp data = aside + chart) in its own colour so the
  // user sees the two legs as grouped contributors to the composite.
  const perpGroupOutline: CSSProperties = shouldDim
    ? {
        boxShadow: `inset 0 0 0 1px ${PERP_COLOR}80, 0 0 18px ${PERP_COLOR}14`,
      }
    : { boxShadow: "none" };
  const predGroupOutline: CSSProperties = shouldDim
    ? {
        boxShadow: `inset 0 0 0 1px ${PRED_COLOR}80, 0 0 18px ${PRED_COLOR}14`,
      }
    : { boxShadow: "none" };
  // Inverse: composite + bottom sections stay muted until both positions
  // are simulated. No hover override — strict on completion.
  const composeDim: CSSProperties = {
    opacity: derived ? 1 : 0.5,
    transition: "opacity 220ms ease",
  };

  const execution = useCompositeExecution();

  // Reset execution state when the user closes the panel or changes a leg.
  useEffect(() => {
    if (!createOpen && execution.state.stage !== "idle") {
      execution.reset();
    }
  }, [createOpen, execution]);

  /**
   * Top-bar SIMULATE button.
   *
   * Auto-selects a BTC perp + a BTC-related Polymarket question whose YES/NO
   * prices aren't pinned at 0/100. After both markets are set, an effect
   * (below) initialises both legs in simulate mode so the composite chart
   * renders without further interaction.
   */
  const [simulating, setSimulating] = useState(false);
  const pendingAutoSimulate = useRef(false);

  const handleSimulate = useCallback(async () => {
    // Already configured — just (re-)init both legs.
    if (selectedPerp && selectedPred) {
      pendingAutoSimulate.current = true;
      // Force the auto-simulate effect to re-run by toggling, by setting
      // legs back to null — the reset effects will fire and our auto-init
      // will follow.
      if (perpLeg) setPerpLeg(null);
      if (predLeg) setPredLeg(null);
      // If neither was clobbered, init directly here.
      if (!perpLeg) initPerpLeg();
      if (!predLeg) initPredLeg();
      setPerpMode("simulate");
      setPredMode("simulate");
      return;
    }

    setSimulating(true);
    pendingAutoSimulate.current = true;
    try {
      const tasks: Promise<void>[] = [];
      if (!selectedPerp) {
        tasks.push(
          fetchBtcPerp().then((p) => {
            if (p) setSelectedPerp(p);
          }),
        );
      }
      if (!selectedPred) {
        tasks.push(
          fetchBtcPrediction({
            gammaUrl: `${window.location.origin}/api/polymarket`,
            clobUrl: `${window.location.origin}/api/polymarket`,
          }).then((p) => {
            if (p) setSelectedPred(p);
          }),
        );
      }
      await Promise.all(tasks);
    } catch {
      pendingAutoSimulate.current = false;
    } finally {
      setSimulating(false);
    }
  }, [selectedPerp, selectedPred, perpLeg, predLeg, initPerpLeg, initPredLeg]);

  /**
   * After SIMULATE-driven market selection lands in state, the per-market
   * reset effects above will null the legs. Once both markets are present
   * AND the auto-simulate flag is set, kick the legs into simulate mode.
   */
  useEffect(() => {
    if (!pendingAutoSimulate.current) return;
    if (!selectedPerp || !selectedPred) return;
    // The reset effects might have already fired in the same render — so the
    // legs may be null at this point. Initialise them now.
    if (!perpLeg) {
      setPerpLeg(defaultPerpLeg(selectedPerp));
    }
    if (!predLeg) {
      setPredLeg(defaultPredLeg(selectedPred, selectedPerp));
    }
    setPerpMode("simulate");
    setPredMode("simulate");
    // Skip the wizard — the user explicitly asked for an auto-built composite.
    setDesktopStage("trading");
    pendingAutoSimulate.current = false;
  }, [selectedPerp, selectedPred, perpLeg, predLeg]);

  const createPanel =
    createOpen && derived && perpLeg && predLeg && selectedPerp && selectedPred ? (
      <CreateCompositePanel
        perp={perpLeg}
        pred={predLeg}
        perpMarket={selectedPerp}
        predMarket={selectedPred}
        mark={derived.mark}
        maxGain={derived.maxGain}
        maxLoss={derived.maxLoss}
        onClose={() => setCreateOpen(false)}
        executionState={execution.state}
        walletReady={execution.ready}
        onConfirm={() =>
          execution.execute({
            perp: perpLeg,
            pred: predLeg,
            perpMarket: selectedPerp,
            predMarket: selectedPred,
          })
        }
      />
    ) : null;

  const mathPanel =
    mathOpen && derived && perpLeg && predLeg && selectedPerp && payoff ? (
      <CompositionMathPanel
        perp={perpLeg}
        pred={predLeg}
        perpSymbol={selectedPerp.coin}
        mark={derived.mark}
        payoff={payoff}
        onClose={() => setMathOpen(false)}
      />
    ) : null;

  const perpsAside = (
    <aside
      style={{
        width: isMobile ? "100%" : "clamp(240px, 25vw, 420px)",
        display: "flex",
        flexDirection: "column",
        borderBottom: isMobile ? "1px solid var(--line-2)" : undefined,
        background: "var(--bg-1)",
        minWidth: 0,
      }}
    >
      <div
        {...dimHandlers}
        style={{
          ...(isMobile ? {} : { flex: 2.75, minHeight: 0 }),
          display: "flex",
          flexDirection: "column",
          position: "relative",
          ...dim,
          ...perpGroupOutline,
        }}
      >
        <div style={panelHeader}>
          <PanelTitle label="PERPS" color={PERP_COLOR} />
          <ExploreCTA onClick={() => setPerpsModalOpen(true)} />
        </div>
        <PerpsPanel
          selected={selectedPerp}
          leg={perpLeg}
          mode={perpMode}
          onModeChange={setPerpMode}
          onLegChange={setPerpLeg}
          onSimulateInit={initPerpLeg}
          onExplore={() => setPerpsModalOpen(true)}
        />
      </div>
      {!isMobile && (
        <div
          style={{
            flex: 7.5,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {mathPanel}
        </div>
      )}
      {isMobile && mathPanel}
    </aside>
  );

  const predsAside = (
    <aside
      style={{
        width: isMobile ? "100%" : "clamp(240px, 25vw, 420px)",
        display: "flex",
        flexDirection: "column",
        borderTop: isMobile ? "1px solid var(--line-2)" : undefined,
        background: "var(--bg-1)",
        minWidth: 0,
      }}
    >
      <div
        {...dimHandlers}
        style={{
          ...(isMobile ? {} : { flex: 2.75, minHeight: 0 }),
          display: "flex",
          flexDirection: "column",
          position: "relative",
          ...dim,
          ...predGroupOutline,
        }}
      >
        <div style={panelHeader}>
          <PanelTitle label="PREDICTIONS" color={PRED_COLOR} />
          <ExploreCTA onClick={() => setPredsModalOpen(true)} />
        </div>
        <PredictionsPanel
          selected={selectedPred}
          leg={predLeg}
          mode={predMode}
          perp={selectedPerp}
          perpSymbol={selectedPerp?.coin}
          onModeChange={setPredMode}
          onLegChange={setPredLeg}
          onSimulateInit={initPredLeg}
          onExplore={() => setPredsModalOpen(true)}
        />
      </div>
      {!isMobile && (
        <div
          style={{
            flex: 7.5,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {createPanel}
        </div>
      )}
      {isMobile && createPanel}
    </aside>
  );

  const perpChartSection = (
    <div
      {...dimHandlers}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderRight: isMobile ? undefined : "1px solid var(--line-2)",
        borderBottom: isMobile ? "1px solid var(--line-2)" : undefined,
        minWidth: 0,
        minHeight: isMobile ? 280 : 0,
        position: "relative",
        ...dim,
        ...perpGroupOutline,
      }}
    >
      <ChartHeader
        title="PERP CHART"
        subtitle={
          <img
            src="/hyperliquid/wordmark/wordmark-white.svg"
            alt="Hyperliquid"
            style={{ height: 11, opacity: 0.55, display: "block" }}
          />
        }
        color={selectedPerp ? PERP_COLOR : undefined}
      />
      <PerpChartView selected={selectedPerp} leg={perpLeg} />
    </div>
  );

  const predChartSection = (
    <div
      {...dimHandlers}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: isMobile ? 280 : 0,
        position: "relative",
        ...dim,
        ...predGroupOutline,
      }}
    >
      <ChartHeader
        title="PREDICTION CHART"
        subtitle={
          <img
            src="/polymarket/logos/logo-white.svg"
            alt="Polymarket"
            style={{ height: 11, opacity: 0.55, display: "block" }}
          />
        }
        color={selectedPred ? PRED_COLOR : undefined}
      />
      <PredictionChartView selected={selectedPred} leg={predLeg} />
    </div>
  );

  const compositionSection = (
    <div
      style={{
        flex: 1,
        minHeight: isMobile ? 360 : 0,
        display: "flex",
        flexDirection: "column",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 1px 0 rgba(0,0,0,0.5)",
      }}
    >
      <div
        style={{
          ...sectionHeader,
          position: "relative",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          {derived && perpLeg && predLeg && selectedPerp && (
            <CompositionMathTrigger
              open={mathOpen}
              onToggle={() => setMathOpen((v) => !v)}
              label="HOW DOES IT WORK?"
            />
          )}
        </div>
        <span
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            whiteSpace: "nowrap",
          }}
        >
          Combined as a &quot;Composite&quot;
        </span>
        <button
          type="button"
          disabled={!derived}
          onClick={() => derived && setCreateOpen((v) => !v)}
          style={createCompositeBtn(!!derived, createOpen)}
          onMouseEnter={(e) => {
            if (!derived) return;
            e.currentTarget.style.background = "var(--fg-max)";
            e.currentTarget.style.color = "var(--bg-0)";
          }}
          onMouseLeave={(e) => {
            if (!derived) return;
            e.currentTarget.style.background = createOpen
              ? "var(--fg-max)"
              : "var(--fg-hi)";
            e.currentTarget.style.color = "var(--bg-0)";
          }}
        >
          {createOpen ? "REVIEWING…" : "CREATE COMPOSITE"}
        </button>
      </div>
      {derived && perpLeg && predLeg ? (
        <div style={{ flex: 1, minHeight: 0, padding: 8 }}>
          <CompositeChart
            points={payoff!}
            mark={derived.mark}
            threshold={predLeg.threshold}
            liquidation={perpLeg.liquidation}
            highlight={
              activeScenario
                ? {
                    price: activeScenario.price,
                    label: activeScenario.label,
                  }
                : null
            }
          />
        </div>
      ) : (
        <Placeholder
          label={
            selectedPerp && selectedPred
              ? "SIMULATE A POSITION ON BOTH SIDES TO COMPOSE"
              : "SELECT A PERP AND A PREDICTION TO COMPOSE"
          }
        />
      )}
    </div>
  );

  const bottomSection = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        minHeight: isMobile ? undefined : 220,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          borderRight: isMobile
            ? undefined
            : "1px solid rgba(255,255,255,0.1)",
          borderBottom: isMobile
            ? "1px solid rgba(255,255,255,0.1)"
            : undefined,
          boxShadow: isMobile
            ? "0 1px 0 rgba(0,0,0,0.5)"
            : "1px 0 0 rgba(0,0,0,0.5)",
          minHeight: isMobile ? 240 : 0,
        }}
      >
        <ChartHeader title="RISK / REWARD" />
        {derived ? (
          <RiskRewardTiles
            maxGain={derived.maxGain}
            maxLoss={derived.maxLoss}
            atMark={derived.atMark}
            breakevens={derived.breakevens}
            strategy={derived.strategy}
          />
        ) : (
          <Placeholder label="RISK / REWARD" />
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: isMobile ? 240 : 0,
        }}
      >
        <ChartHeader
          title="SCENARIOS"
          subtitle={derived ? "P&L AT KEY PRICES" : undefined}
        />
        {derived ? (
          <ScenariosTable
            rows={derived.scenarios}
            activePrice={activeScenario?.price ?? null}
            onHover={setHoverScenario}
            onSelect={(s) =>
              setPinnedScenario((prev) =>
                prev && prev.price === s.price ? null : s,
              )
            }
          />
        ) : (
          <Placeholder label="SCENARIOS" />
        )}
      </div>
    </div>
  );

  const modals = (
    <>
      <HyperliquidMarketsModal
        open={perpsModalOpen}
        onClose={() => setPerpsModalOpen(false)}
        onSelect={setSelectedPerp}
      />
      <PolymarketMarketsModal
        open={predsModalOpen}
        onClose={() => setPredsModalOpen(false)}
        onSelect={setSelectedPred}
      />
    </>
  );

  // --------------------------------------------------------------------------
  // Layouts
  // --------------------------------------------------------------------------

  if (isMobile) {
    return (
      <>
        <MobileApp
          selectedPerp={selectedPerp}
          selectedPred={selectedPred}
          onOpenPerpsModal={() => setPerpsModalOpen(true)}
          onOpenPredsModal={() => setPredsModalOpen(true)}
          perpLeg={perpLeg}
          predLeg={predLeg}
          perpMode={perpMode}
          predMode={predMode}
          setPerpMode={setPerpMode}
          setPredMode={setPredMode}
          setPerpLeg={setPerpLeg}
          setPredLeg={setPredLeg}
          initPerpLeg={initPerpLeg}
          initPredLeg={initPredLeg}
          payoff={payoff}
          derived={derived}
          activeScenario={activeScenario}
          setHoverScenario={setHoverScenario}
          setPinnedScenario={setPinnedScenario}
          mathOpen={mathOpen}
          setMathOpen={setMathOpen}
          createOpen={createOpen}
          setCreateOpen={setCreateOpen}
          executionState={execution.state}
          walletReady={execution.ready}
          onSimulate={handleSimulate}
          onConfirmExecute={() => {
            if (perpLeg && predLeg && selectedPerp && selectedPred) {
              execution.execute({
                perp: perpLeg,
                pred: predLeg,
                perpMarket: selectedPerp,
                predMarket: selectedPred,
              });
            }
          }}
        />
        {modals}
      </>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        background: "var(--bg-0)",
        color: "var(--fg-hi)",
      }}
    >
      <TopBar
        onSimulate={handleSimulate}
        onLogoClick={() => {
          if (revealingComposite) return;
          setDesktopStage("landing");
        }}
      />

      {desktopStage === "landing" && (
        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {revealingComposite && <GoldSweep />}
          <DesktopLanding
            selectedPerp={selectedPerp}
            selectedPred={selectedPred}
            onPickPerp={() => setPerpsModalOpen(true)}
            onPickPred={() => setPredsModalOpen(true)}
            revealingTrading={revealingComposite}
            onCompose={() => {
              if (revealingComposite) return;
              setRevealingComposite(true);
              window.setTimeout(() => {
                if (!perpLeg && selectedPerp) {
                  setPerpLeg(defaultPerpLeg(selectedPerp));
                }
                if (!predLeg && selectedPred) {
                  setPredLeg(defaultPredLeg(selectedPred, selectedPerp));
                }
                setPerpMode("simulate");
                setPredMode("simulate");
                setDesktopStage("trading");
              }, 320);
              window.setTimeout(() => setRevealingComposite(false), 1000);
            }}
          />
        </div>
      )}

      {desktopStage === "trading" && (
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            animation: revealingComposite
              ? "hh-reveal-in 580ms cubic-bezier(0.16, 1, 0.3, 1) forwards"
              : undefined,
          }}
        >
          {revealingComposite && <GoldSweep />}
          <TradingBreadcrumb
            selectedPerp={selectedPerp}
            selectedPred={selectedPred}
            onEdit={() => setDesktopStage("landing")}
          />
          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            {perpsAside}
            <main
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {derived && perpLeg && predLeg && selectedPerp && selectedPred && payoff ? (
                <DesktopCompositeView
                  payoff={payoff}
                  derived={derived}
                  perpLeg={perpLeg}
                  predLeg={predLeg}
                  perpCoin={selectedPerp.coin}
                  perpEntry={perpLeg.entry}
                  predQuestion={selectedPred.question}
                  predOutcome={predLeg.outcome}
                  activeScenario={activeScenario}
                  setHoverScenario={setHoverScenario}
                  setPinnedScenario={setPinnedScenario}
                  mathOpen={mathOpen}
                  setMathOpen={setMathOpen}
                  createOpen={createOpen}
                  setCreateOpen={setCreateOpen}
                  executionState={execution.state}
                />
              ) : (
                <Placeholder
                  label={
                    selectedPerp && selectedPred
                      ? "SIMULATE A POSITION ON BOTH SIDES TO COMPOSE"
                      : "SELECT A PERP AND A PREDICTION TO COMPOSE"
                  }
                />
              )}
            </main>
            {predsAside}
          </div>
        </div>
      )}

      {modals}
    </div>
  );
}

// --------------------------------------------------------------------------
// Trading breadcrumb — thin band under TopBar showing the selected legs and
// an "edit" button that bounces the user back to the selection wizard.
// --------------------------------------------------------------------------

function TradingBreadcrumb({
  selectedPerp,
  selectedPred,
  onEdit,
}: {
  selectedPerp: SelectedPerp | null;
  selectedPred: SelectedPred | null;
  onEdit: () => void;
}) {
  const perpLabel = selectedPerp ? selectedPerp.coin : "—";
  const predQ = selectedPred?.question ?? "—";
  const predLabel =
    predQ.length > 56 ? `${predQ.slice(0, 53)}…` : predQ;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "8px 18px",
        borderBottom: "1px solid var(--line-2)",
        background: "var(--bg-0)",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 9,
          letterSpacing: "0.22em",
          color: "var(--fg-dim)",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        COMPOSING
      </span>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          color: PERP_COLOR,
          fontWeight: 600,
          letterSpacing: "0.04em",
        }}
      >
        {perpLabel}
      </span>
      <span style={{ color: "var(--fg-dim)", fontSize: 12 }}>×</span>
      <span
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 12,
          color: PRED_COLOR,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={predQ}
      >
        {predLabel}
      </span>
      <button
        type="button"
        onClick={onEdit}
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 9,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "var(--fg-meta)",
          background: "transparent",
          border: "1px solid var(--line-3)",
          padding: "5px 10px",
          borderRadius: 2,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--fg-max)";
          e.currentTarget.style.borderColor = "var(--fg-meta)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--fg-meta)";
          e.currentTarget.style.borderColor = "var(--line-3)";
        }}
      >
        ← Edit Legs
      </button>
    </div>
  );
}
